
import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { 
  Card, CardContent, CardHeader, CardTitle 
} from "@/components/ui/card";
import {
  Button
} from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { RecipeDetails } from "@/components/RecipeDetails";
import { GroceryList } from "@/components/GroceryList";
import { Search, ChevronRight, ChevronDown } from "lucide-react";

interface SavedRecipe {
  id: string;
  user_id: string;
  recipe_name: string;
  ingredients: string[];
  instructions: string;
  created_at: string;
  updated_at: string;
}

export function SavedRecipesTab() {
  const { user } = useAuth();
  const [recipes, setRecipes] = useState<SavedRecipe[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage] = useState(5);
  const [expandedRecipe, setExpandedRecipe] = useState<string | null>(null);
  const [showGroceryFor, setShowGroceryFor] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      fetchRecipes();
    }
  }, [user]);

  const fetchRecipes = async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('recipes')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
        
      if (error) {
        console.error("Error fetching recipes:", error);
        toast({
          title: "Error",
          description: "Failed to load saved recipes",
          variant: "destructive"
        });
      }
      
      setRecipes(data || []);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleRecipeExpansion = (recipeId: string) => {
    if (expandedRecipe === recipeId) {
      setExpandedRecipe(null);
    } else {
      setExpandedRecipe(recipeId);
    }
  };

  const deleteRecipe = async (recipeId: string) => {
    if (!user) return;
    
    try {
      const { error } = await supabase
        .from('recipes')
        .delete()
        .eq('id', recipeId);
        
      if (error) {
        console.error("Error deleting recipe:", error);
        toast({
          title: "Error",
          description: "Failed to delete recipe",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Success",
          description: "Recipe deleted successfully",
        });
        fetchRecipes();
      }
    } catch (error) {
      console.error("Error deleting recipe:", error);
    }
  };

  // Filter and paginate recipes
  const filteredRecipes = recipes.filter(recipe => 
    recipe.recipe_name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const totalPages = Math.ceil(filteredRecipes.length / perPage);
  
  const paginatedRecipes = filteredRecipes.slice(
    (currentPage - 1) * perPage, 
    currentPage * perPage
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <CardTitle>Saved Recipes</CardTitle>
          </div>
        </div>
        
        <div className="flex items-center mt-2 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={16} />
          <Input
            placeholder="Search recipes..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            className="pl-9"
          />
        </div>
      </CardHeader>
      
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : paginatedRecipes.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">No saved recipes found.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {paginatedRecipes.map((recipe) => (
              <Card key={recipe.id}>
                <div 
                  className="flex justify-between items-center p-4 cursor-pointer"
                  onClick={() => toggleRecipeExpansion(recipe.id)}
                >
                  <div>
                    <h3 className="font-medium">{recipe.recipe_name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {new Date(recipe.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteRecipe(recipe.id);
                      }}
                    >
                      Delete
                    </Button>
                    {expandedRecipe === recipe.id ? (
                      <ChevronDown className="h-5 w-5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    )}
                  </div>
                </div>
                
                {expandedRecipe === recipe.id && (
                  <div>
                    <RecipeDetails recipe={{
                      recipeName: recipe.recipe_name,
                      ingredients: recipe.ingredients,
                      instructions: recipe.instructions
                    }} />

                    <div className="p-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setShowGroceryFor(showGroceryFor === recipe.id ? null : recipe.id)}
                        className="mb-4"
                      >
                        {showGroceryFor === recipe.id ? "Hide Grocery List" : "Show Grocery List"}
                      </Button>
                      
                      {showGroceryFor === recipe.id && (
                        <GroceryList 
                          title={`Ingredients for ${recipe.recipe_name}`} 
                          ingredients={recipe.ingredients}
                        />
                      )}
                    </div>
                  </div>
                )}
              </Card>
            ))}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
